<template>
	<ion-app>
		<!-- Conditional layout wrapper around the router outlet -->
		<AuthenticatedLayout v-if="!isAnonymousRoute">
			<ion-router-outlet id="main-content" />
		</AuthenticatedLayout>

		<AnonymousLayout v-else>
			<ion-router-outlet id="main-content" />
		</AnonymousLayout>

		<!-- Global components that should always be present -->
		<ReconnectingIndicator />
	</ion-app>

	<!-- Global development tools -->
	<VueQueryDevtools />
</template>

<script setup lang="ts">
	import { IonApp, IonRouterOutlet } from "@ionic/vue";
	import { VueQueryDevtools } from "@tanstack/vue-query-devtools";
	import ReconnectingIndicator from "@/components/ReconnectingIndicator.vue";
	import AuthenticatedLayout from "@/views/Layout/AuthenticatedLayout.vue";
	import AnonymousLayout from "@/views/Layout/AnonymousLayout.vue";
	import { computed, watch } from "vue";
	import { useRoute } from "vue-router";
	import * as Sentry from "@sentry/vue";
	import { useMyUser } from "seedgreen-shared/queries/user";
	import { usePostHog } from "@/composables/usePosthog";
	import { useMyCustomer } from "seedgreen-shared/queries/customer";

	const { posthog } = usePostHog();

	const { myUser } = useMyUser();
	const { myCustomer } = useMyCustomer();

	// Determine if current route is anonymous
	const route = useRoute();
	const isAnonymousRoute = computed(() => route.meta?.anonymous === true);

	watch(myUser, () => {
		// this sets up the user data for Sentry when the user ref changes
		Sentry.setContext(
			"User Data",
			(myUser.value ?? {}) as Record<string, string>,
		);
		Sentry.setUser({
			id: myUser.value?.id ?? "",
			username: myUser.value?.name ?? "",
			email: myUser.value?.userName ?? "",
			customerId: myUser.value?.customerId ?? "",
		});
		if (myUser.value?.id) {
			posthog.identify(myUser.value.id.toString(), {
				id: myUser.value.id,
				name: myUser.value.name,
				email: myUser.value.userName,
				customerId: myUser.value.customerId,
				locale: myUser.value.locale,
				internalUser: myUser.value.internalUser,
				unlisted: myUser.value.unlisted,
				userAgreementVersion: myUser.value.userAgreementString,
			});
		}
	});

	watch(myCustomer, () => {
		if (!myCustomer.value || !myUser.value) return;
		const customer = myUser.value?.internalUser
			? "GVA"
			: myCustomer.value.name;
		posthog.capture("$set", {
			$set: { customer: customer },
		});
	});
</script>
